import React, { useState, useRef } from 'react';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbInput from '../controls/HrnbInput';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbSelect from '../controls/HrnbSelect';
import AudioPlayer from './AudioPlayer';
import '../../css/components/TrackUploader.css';
import { battlesApi, worksApi, textsApi } from '../../api';

/**
 * Компонент загрузки трека в раунд баттла
 * Поддерживает:
 * - Текстовый трек (без аудиофайла)
 * - Аудио трек с загрузкой файла (multipart)
 * - Аудио трек с внешним URL
 *
 * @component
 * @param {Object} props
 * @param {string} props.battleId - UUID баттла
 * @param {string} props.roundId - UUID раунда
 * @param {string} props.authorId - UUID профиля автора (участника)
 * @param {string} props.battlePairId - UUID пары баттла (опционально)
 * @param {Function} props.onSuccess - Колбэк при успешной загрузке
 * @param {Function} props.onCancel - Колбэк при отмене
 * @param {boolean} props.isAdmin - Права администратора
 * @param {import('../../model/battle').BattleFormat} [props.battleFormat] - Формат баттла (AUDIO|TEXT)
 */
export default function TrackUploader({
  battleId,
  roundId,
  authorId,
  battlePairId,
  onSuccess,
  onCancel,
  isAdmin = false,
  battleFormat = 'AUDIO'
}) {
  const [trackType, setTrackType] = useState(battleFormat === 'TEXT' ? 'text' : 'audio'); // 'text' | 'audio'
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'url' (только для audio)
  const [selectedFile, setSelectedFile] = useState(null);
  const [trackUrl, setTrackUrl] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const [trackDescription, setTrackDescription] = useState('');
  const [trackText, setTrackText] = useState('');
  const [duration, setDuration] = useState('');
  const [format, setFormat] = useState('MP3');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const audioFormats = [
    { value: 'MP3', label: 'MP3' },
    { value: 'WAV', label: 'WAV' },
    { value: 'OGG', label: 'OGG' },
    { value: 'FLAC', label: 'FLAC' },
    { value: 'AAC', label: 'AAC' },
    { value: 'M4A', label: 'M4A' }
  ];

  const validateForm = () => {
    if (!trackTitle.trim()) {
      setError('Укажите название трека');
      return false;
    }

    if (!trackText.trim()) {
      setError('Введите текст трека');
      return false;
    }

    // Для аудио-режима дополнительная проверка
    if (trackType === 'audio') {
      if (uploadMode === 'file') {
        if (!selectedFile) {
          setError('Выберите аудиофайл');
          return false;
        }

        // Проверка размера файла (макс 30MB)
        const maxSize = 30 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
          setError(`Размер файла превышает лимит (макс. 30 МБ)`);
          return false;
        }

        // Проверка формата файла
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/mp4'];
        if (!allowedTypes.includes(selectedFile.type)) {
          setError(`Неподдерживаемый формат файла. Допустимы: MP3, WAV, OGG, FLAC, AAC, M4A`);
          return false;
        }
      } else {
        if (!trackUrl.trim()) {
          setError('Укажите URL трека');
          return false;
        }

        // Простая валидация URL
        try {
          new URL(trackUrl);
        } catch {
          setError('Некорректный URL');
          return false;
        }
      }

      if (!duration || isNaN(duration) || parseInt(duration) <= 0) {
        setError('Укажите длительность трека в секундах');
        return false;
      }
    }

    if (!authorId) {
      console.error('[TrackUploader] authorId is missing');
      setError('Не указан автор трека. Убедитесь, что в раунде есть участники.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    console.log('[TrackUploader] handleSubmit called', {
      battleId,
      roundId,
      authorId,
      battlePairId,
      trackType,
      uploadMode,
      trackTitle,
      selectedFile,
      duration
    });

    if (!validateForm()) {
      console.log('[TrackUploader] Validation failed');
      return;
    }

    setLoading(true);

    try {
      console.log('[TrackUploader] Starting upload...', {
        battleId,
        roundId,
        authorId,
        battlePairId,
        trackType,
        uploadMode,
        trackTitle,
        duration,
        format
      });

      if (trackType === 'text') {
        // Текстовый трек - создаём Text, затем Work
        console.log('[TrackUploader] Creating text work...');

        // 1. Создаём текст
        const textData = {
          battlePersonId: authorId,
          title: trackTitle.trim(),
          description: trackDescription.trim() || null,
          content: trackText.trim(),
          status: 'SUBMITTED'
        };

        console.log('[TrackUploader] Creating text:', textData);
        const textResult = await textsApi.createText(textData);
        console.log('[TrackUploader] Text created:', textResult);

        // 2. Создаём работу с текстом
        const workData = {
          battlePersonId: authorId,
          roundId: roundId,
          textId: textResult.id,
          status: 'SUBMITTED'
        };

        console.log('[TrackUploader] Creating work with text:', workData);
        const workResult = await worksApi.createWork(workData);
        console.log('[TrackUploader] Work created successfully:', workResult);

        if (onSuccess) {
          onSuccess(workResult);
        }
      } else if (uploadMode === 'file') {
        // Аудио трек с файлом через FormData - старый способ (legacy)
        // В новой версии сначала загружаем файл, создаём Track, затем Work
        const formData = new FormData();
        formData.append('track', selectedFile);
        formData.append('title', trackTitle.trim());
        formData.append('description', trackDescription.trim());
        formData.append('text', trackText.trim());
        formData.append('authorId', authorId);
        formData.append('duration', parseInt(duration));
        formData.append('format', format);
        if (battlePairId) {
          formData.append('battlePairId', battlePairId);
        }

        console.log('[TrackUploader] FormData prepared, calling uploadTrack API...');
        // Логируем ключи FormData для отладки
        for (let pair of formData.entries()) {
          const value = pair[1];
          console.log('[TrackUploader] FormData entry:', pair[0], value, `(${typeof value}${value instanceof File ? `, size: ${value.size}` : ''})`);
        }

        // Загружаем трек через API (возвращает WorkResponse)
        const workResult = await battlesApi.uploadTrack(
          battleId,
          roundId,
          formData,
          (percent) => setProgress(percent)
        );
        console.log('[TrackUploader] Work uploaded:', workResult);

        if (onSuccess) {
          onSuccess(workResult);
        }
      } else {
        // Аудио трек с внешним URL
        const payload = {
          title: trackTitle.trim(),
          description: trackDescription.trim(),
          text: trackText.trim(),
          mediaUrl: trackUrl.trim(),
          authorId: authorId,
          duration: parseInt(duration),
          format: format,
          ...(battlePairId && { battlePairId })
        };

        console.log('[TrackUploader] Creating track from URL, payload:', payload);
        // Создаём трек через API (возвращает WorkResponse)
        const workResult = await battlesApi.createTrackByUrl(battleId, roundId, payload);
        console.log('[TrackUploader] Work created:', workResult);

        if (onSuccess) {
          onSuccess(workResult);
        }
      }
    } catch (err) {
      console.error('[TrackUploader] Failed to upload track/work:', err);
      console.error('[TrackUploader] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.message || 'Ошибка при загрузке работы');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setError(null);

      // Создаём URL для превью
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleReset = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setTrackType(battleFormat === 'TEXT' ? 'text' : 'audio');
    setUploadMode('file');
    setSelectedFile(null);
    setTrackUrl('');
    setTrackTitle('');
    setTrackDescription('');
    setTrackText('');
    setDuration('');
    setFormat('MP3');
    setError(null);
    setProgress(0);
    setPreviewUrl(null);
  };

  // Проверка authorId при монтировании
  React.useEffect(() => {
    if (!authorId) {
      console.warn('[TrackUploader] authorId is missing. The component requires a valid authorId to upload tracks.');
    }
  }, [authorId]);

  // Очистка URL при размонтировании
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="track-uploader">
      {/* Переключатель типа трека */}
      <div className="track-uploader__type-toggle">
        <button
          type="button"
          className={`type-btn ${trackType === 'text' ? 'active' : ''}`}
          onClick={() => { setTrackType('text'); handleReset(); }}
          disabled={battleFormat === 'AUDIO'}
        >
          📝 Текстовый трек
        </button>
        <button
          type="button"
          className={`type-btn ${trackType === 'audio' ? 'active' : ''}`}
          onClick={() => { setTrackType('audio'); handleReset(); }}
          disabled={battleFormat === 'TEXT'}
        >
          🎵 Аудио трек
        </button>
      </div>

      <div className="track-uploader__mode-toggle">
        <button
          type="button"
          className={`mode-btn ${uploadMode === 'file' ? 'active' : ''}`}
          onClick={() => { setUploadMode('file'); handleReset(); }}
          disabled={trackType === 'text'}
        >
          📁 Загрузить файл
        </button>
        <button
          type="button"
          className={`mode-btn ${uploadMode === 'url' ? 'active' : ''}`}
          onClick={() => { setUploadMode('url'); handleReset(); }}
          disabled={trackType === 'text'}
        >
          🔗 Внешний URL
        </button>
      </div>

      <form onSubmit={handleSubmit} className="track-uploader__form">
        {error && (
          <HrnbAlert variant="danger" onClose={() => setError(null)}>
            {error}
          </HrnbAlert>
        )}

        <HrnbInput
          label="Название трека"
          value={trackTitle}
          onChange={(e) => setTrackTitle(e.target.value)}
          placeholder="Например: Мой фристайл"
          required
          disabled={loading}
        />

        <HrnbTextArea
          label="Описание"
          value={trackDescription}
          onChange={(e) => setTrackDescription(e.target.value)}
          placeholder="Необязательно"
          rows={3}
          disabled={loading}
        />

        <HrnbTextArea
          label="Текст трека"
          value={trackText}
          onChange={(e) => setTrackText(e.target.value)}
          placeholder="Введите текст трека (обязательно)"
          rows={5}
          required
          disabled={loading}
        />

        {uploadMode === 'file' ? (
          <HrnbFileUpload
            label="Аудиофайл"
            accept="audio/mpeg,audio/wav,audio/ogg,audio/flac,audio/aac,audio/mp4,.mp3,.wav,.ogg,.flac,.aac,.m4a"
            onChange={handleFileChange}
            value={selectedFile ? [selectedFile] : null}
            hint="Допустимые форматы: MP3, WAV, OGG, FLAC, AAC, M4A. Макс. размер: 30 МБ"
            disabled={loading}
          />
        ) : (
          <HrnbInput
            label="URL трека"
            value={trackUrl}
            onChange={(e) => setTrackUrl(e.target.value)}
            placeholder="https://example.com/track.mp3"
            required
            disabled={loading}
          />
        )}

        {/* Превью аудиофайла */}
        {trackType === 'audio' && previewUrl && uploadMode === 'file' && (
          <div className="track-uploader__preview">
            <AudioPlayer
              src={previewUrl}
              title={selectedFile?.name || 'Предпрослушивание'}
              showDownload={false}
            />
          </div>
        )}

        {/* Превью для URL */}
        {trackType === 'audio' && trackUrl && uploadMode === 'url' && (
          <div className="track-uploader__preview">
            <AudioPlayer
              src={trackUrl}
              title="Предпрослушивание"
              showDownload={false}
            />
          </div>
        )}

        {trackType === 'audio' && (
          <div className="track-uploader__row">
            <HrnbInput
              label="Длительность (сек)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="180"
              min="1"
              required
              disabled={loading}
              style={{ flex: 1 }}
            />

            <HrnbSelect
              label="Формат"
              value={format}
              onChange={(value) => setFormat(value)}
              options={audioFormats}
              disabled={loading}
              style={{ flex: 1 }}
            />
          </div>
        )}

        {loading && (
          <div className="track-uploader__progress">
            <HrnbLoader />
            <p>Загрузка трека... {progress > 0 && `(${progress}%)`}</p>

            {/* Прогресс бар */}
            {progress > 0 && (
              <div className="track-uploader__progress-bar">
                <div
                  className="track-uploader__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="track-uploader__actions">
          {onCancel && (
            <HrnbButton
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Отмена
            </HrnbButton>
          )}
          <HrnbButton
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Загрузка...' : trackType === 'text' ? 'Отправить текстовый трек' : uploadMode === 'file' ? 'Загрузить аудио трек' : 'Создать аудио трек'}
          </HrnbButton>
        </div>
      </form>
    </div>
  );
}
